// src/features/portfolio-drift-auditor-pro/store/useProDriftFormStore.ts
//
// Persisted Zustand store for the PRO Drift Auditor input form. Every edit
// (holdings, sliders, cash inflow, lookback) overwrites the single saved
// snapshot in localStorage, so leaving the page -- or refreshing -- and coming
// back restores the user's very latest inputs instead of the hardcoded preset.
//
// Design notes:
//   - Bounds live here (FORM_BOUNDS) and are shared with the form sliders, so
//     the UI range and the hydration clamp can never drift apart. They mirror
//     backend/app/domain/portfolio_drift_auditor_pro/schemas.py.
//   - Hydration is defensive by construction: the storage adapter swallows
//     unavailable-storage / quota / corrupt-JSON errors, and `merge` re-validates
//     every field of the (untrusted) persisted payload, clamping to the current
//     bounds and falling back to defaults per field. Nothing ever throws into
//     the render path.
//   - Row ids are UI-only React keys; they are regenerated from a single
//     module counter on hydration so restored rows can never collide with new ones.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
    clampToBound,
    createSafeLocalStorage,
    isFiniteNumber,
    isRecord,
    type Bound,
} from '../../../shared/utils/persistedForm';
import type { HoldingRow } from '../types';

export const PRO_DRIFT_FORM_STORAGE_KEY = 'forge:pro-drift-auditor-form';
const STORAGE_VERSION = 1;

export const MIN_HOLDINGS = 2;
export const MAX_HOLDINGS = 30;
export const MAX_TICKER_LENGTH = 16;

export const FORM_BOUNDS = {
    initialCapital: { min: 1_000, max: 100_000_000 },
    lookbackYears: { min: 1, max: 20, step: 1 },
    corridorPct: { min: 1, max: 30, step: 1 },
    txCostBps: { min: 0, max: 400, step: 1 },
    capGainsPct: { min: 0, max: 50, step: 1 },
    monthlyCash: { min: 0, max: 100_000_000 },
    cooldownDays: { min: 5, max: 126, step: 1 },
    riskFreePct: { min: 0, max: 10, step: 0.25 },
} as const satisfies Record<string, Bound>;

export const DEFAULT_HOLDINGS: readonly (readonly [string, number])[] = [
    ['SPY', 60],
    ['AGG', 40],
];
export const DEFAULT_LOOKBACK_YEARS = 5;

export interface ProDriftFormValues {
    rows: HoldingRow[];
    initialCapital: number;
    lookbackYears: number;
    corridorPct: number;
    txCostBps: number;
    capGainsPct: number;
    // null = empty input (user cleared it / never typed); submitted as 0.
    monthlyCash: number | null;
    cooldownDays: number;
    riskFreePct: number;
    useLiveWeights: boolean;
}

type ScalarField = Exclude<keyof ProDriftFormValues, 'rows'>;

interface ProDriftFormStore extends ProDriftFormValues {
    setField: <K extends ScalarField>(key: K, value: ProDriftFormValues[K]) => void;
    updateRow: (id: number, patch: Partial<Omit<HoldingRow, 'id'>>) => void;
    removeRow: (id: number) => void;
    addRow: () => void;
    loadPreset: (holdings: readonly (readonly [string, number])[]) => void;
    reset: () => void;
}

let rowSeq = 0;
const makeRow = (ticker: string, weight: number, currentWeight: number | null = null): HoldingRow => ({
    id: ++rowSeq,
    ticker,
    weight,
    currentWeight,
});
const toRows = (holdings: readonly (readonly [string, number])[]): HoldingRow[] =>
    holdings.map(([ticker, weight]) => makeRow(ticker, weight));

const defaultValues = (): ProDriftFormValues => ({
    rows: toRows(DEFAULT_HOLDINGS),
    initialCapital: 100_000,
    lookbackYears: DEFAULT_LOOKBACK_YEARS,
    corridorPct: 5,
    txCostBps: 10,
    capGainsPct: 15,
    monthlyCash: null,
    cooldownDays: 21,
    riskFreePct: 4,
    useLiveWeights: false,
});

// -- Defensive hydration ------------------------------------------------------
const sanitizeRows = (raw: unknown): HoldingRow[] | null => {
    if (!Array.isArray(raw)) return null;
    const rows: HoldingRow[] = [];
    for (const r of raw.slice(0, MAX_HOLDINGS)) {
        if (!isRecord(r) || typeof r.ticker !== 'string') continue;
        const weight = isFiniteNumber(r.weight) && r.weight >= 0 ? r.weight : 0;
        const live = isFiniteNumber(r.currentWeight) && r.currentWeight >= 0 ? r.currentWeight : null;
        rows.push(makeRow(r.ticker.slice(0, MAX_TICKER_LENGTH), weight, live));
    }
    return rows.length >= MIN_HOLDINGS ? rows : null;
};

export const sanitizeFormValues = (raw: unknown): ProDriftFormValues => {
    const d = defaultValues();
    if (!isRecord(raw)) return d;
    const B = FORM_BOUNDS;
    return {
        rows: sanitizeRows(raw.rows) ?? d.rows,
        // Lower bound 0 (not 1,000): restore exactly what was typed; the submit
        // handler reports the minimum instead of silently rewriting the input.
        initialCapital: clampToBound(raw.initialCapital, d.initialCapital, { min: 0, max: B.initialCapital.max }),
        lookbackYears: clampToBound(raw.lookbackYears, d.lookbackYears, B.lookbackYears),
        corridorPct: clampToBound(raw.corridorPct, d.corridorPct, B.corridorPct),
        txCostBps: clampToBound(raw.txCostBps, d.txCostBps, B.txCostBps),
        capGainsPct: clampToBound(raw.capGainsPct, d.capGainsPct, B.capGainsPct),
        monthlyCash: isFiniteNumber(raw.monthlyCash) ? clampToBound(raw.monthlyCash, 0, B.monthlyCash) : null,
        cooldownDays: clampToBound(raw.cooldownDays, d.cooldownDays, B.cooldownDays),
        riskFreePct: clampToBound(raw.riskFreePct, d.riskFreePct, B.riskFreePct),
        useLiveWeights: typeof raw.useLiveWeights === 'boolean' ? raw.useLiveWeights : d.useLiveWeights,
    };
};

// localStorage adapter that never throws: private mode / disabled storage,
// quota errors and corrupt JSON all degrade to "no saved state".
const safeLocalStorage = createSafeLocalStorage<ProDriftFormValues>();

export const useProDriftFormStore = create<ProDriftFormStore>()(
    persist(
        (set) => ({
            ...defaultValues(),
            setField: (key, value) => set({ [key]: value } as Partial<ProDriftFormValues>),
            updateRow: (id, patch) =>
                set((s) => ({ rows: s.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) })),
            removeRow: (id) =>
                set((s) => (s.rows.length <= MIN_HOLDINGS ? s : { rows: s.rows.filter((r) => r.id !== id) })),
            addRow: () =>
                set((s) => (s.rows.length >= MAX_HOLDINGS ? s : { rows: [...s.rows, makeRow('', 10)] })),
            loadPreset: (holdings) => set({ rows: toRows(holdings) }),
            reset: () => set(defaultValues()),
        }),
        {
            name: PRO_DRIFT_FORM_STORAGE_KEY,
            version: STORAGE_VERSION,
            storage: safeLocalStorage,
            // Persist ONLY the serialisable form values, never the actions.
            partialize: (s): ProDriftFormValues => ({
                rows: s.rows,
                initialCapital: s.initialCapital,
                lookbackYears: s.lookbackYears,
                corridorPct: s.corridorPct,
                txCostBps: s.txCostBps,
                capGainsPct: s.capGainsPct,
                monthlyCash: s.monthlyCash,
                cooldownDays: s.cooldownDays,
                riskFreePct: s.riskFreePct,
                useLiveWeights: s.useLiveWeights,
            }),
            // Older / foreign schema versions are re-validated field-by-field.
            migrate: (persisted) => sanitizeFormValues(persisted),
            merge: (persisted, current) => ({ ...current, ...sanitizeFormValues(persisted) }),
        },
    ),
);
